import { useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './components/Modal';

const SplashScreen = ({isOpen, setIsOpen}) => {
  useEffect(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const btnStyle = {
    float: 'right',
  };

  const borderBox = {
    backgroundColor: '#d1ecf1',
    padding: '5px',
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader>About this Smoke Control Area map</ModalHeader>
      <ModalBody>
        <div>
          <p className="pb-4 text-sm md:text-md font-sans">
            This interactive tool allows you to explore the location of Smoke
            Control Areas and exemptions in <strong>England only</strong>.
            Download the guidebook on how to use this tool{' '}
            <a
              href="https://uk-air.defra.gov.uk/assets/documents/SmokeControlArea_guidebook_2022.pdf"
              className="underline"
            >
              here
            </a>{' '}
          </p>
          <p className="pb-4 text-sm md:text-md font-sans">
            A{' '}
            <a
              href="https://www.gov.uk/smoke-control-area-rules"
              className="underline"
            >
              smoke control area
            </a>{' '}
            is an area where people and businesses must not:
          </p>

          <ul className="list-inside list-disc">
            <li>emit smoke from a chimney</li>
            <li>
              buy or sell unauthorised fuel for use in a smoke control area
              unless it’s used in an{' '}
              <a
                href="https://smokecontrol.defra.gov.uk/appliances.php?country=england"
                className="underline"
              >
                ‘exempt’ appliance
              </a>{' '}
              (appliances which are approved to use in smoke control areas)
            </li>
          </ul>
          <br />

          <p className="pb-4 text-sm md:text-md font-sans">
            This data has been provided to Defra by local authorities.{' '}
            <strong>
              This data is indicative only and you should always{' '}
              <a
                href="https://www.gov.uk/find-your-local-council"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                contact your local council{' '}
              </a>
              to confirm if you live in a Smoke Control Area.
            </strong>
          </p>
          <p className="pb-4 text-sm md:text-md font-sans">
            If you are a local authority who would like to inform Defra of the
            presence of or any changes to Smoke Control Areas in your authority,
            please get in touch at{' '}
            <a href="mailto:sca@ricardo.com" className="underline">
              sca@ricardo.com
            </a>
          </p>
          <div style={borderBox}>
            <h4 className="pt-4 text-lg font-sans mb-2">Important notes:</h4>
            <p className="pb-4 text-sm md:text-md font-sans">
              The Smoke Control Order boundaries are indicative only.
            </p>

            <p className="pb-4 text-sm md:text-md font-sans">
              In some cases, GIS has been used to recreate boundaries defined in
              original Smoke Control Orders. Please be advised that map features
              present at the time the Orders were made may have since been
              amended which may impact accuracy.
            </p>

            <p className="pb-4 text-sm md:text-md font-sans">
              You should always{' '}
              <a
                href="https://www.gov.uk/find-your-local-council"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                contact your local council{' '}
              </a>
              to find out if you live in a smoke control area
            </p>
          </div>

          <hr />
          <h4 className="pt-4 text-lg font-sans mb-2 font-bold">
            Get help using this tool
          </h4>
          <p className="pb-4 text-sm md:text-md font-sans">
            For any questions about this tool, please contact{' '}
            <a href="mailto:aqinfo@ricardo.com" className="underline">
              aqinfo@ricardo.com
            </a>
          </p>

          <p className="pb-4 text-sm md:text-md font-sans">
            For more information on Smoke Control Areas, including what you can
            burn, please see this{' '}
            <a
              href="https://www.gov.uk/smoke-control-area-rules"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              gov.uk page.{' '}
            </a>
          </p>
          <hr />
          <div className="font-sans text-sm mb:text-base">
            <h4 className="pt-2 text-lg font-sans mb-2 font-bold">
              Licencing information
            </h4>
            <p className="pb-2">
              Boundaries represented in this mapping application are an outline
              of adjoining Smoke Control Orders and do not show the extent of
              individual Orders, unless geographically separate.
            </p>

            <p className="pb-2">
              This data is derived from a number of sources licensed under the
              Public Sector Geospatial Agreement (PSGA) from:
            </p>
            <ul className="list-inside list-disc">
              <li>
                Ordnance Survey (GB) data © Crown copyright and database right
                2022
              </li>
              <li>
                Royal Mail (GB) data © Royal Mail copyright and database right
                2022
              </li>
            </ul>
            <br/>
            <p className="pb-2">As well as:</p>
            <ul className="list-inside list-disc">
              <li>
                Office for National Statistics licensed under the Open
                Government Licence v.3.0
              </li>
            </ul>
            <br/>
            <p className="pb-2">
              The Smoke Control Order area boundaries are provided by local
              authorities either in digital or paper formats for Orders going
              back as far as the 1960s based on information from Ordnance Survey
              (OS), Royal mail and other organisations.
            </p>
            <p className="pb-2 font-semibold">
              &copy; Crown copyright and database rights licensed under Defra's
              Public Sector Mapping Agreement with Ordnance Survey (licence No.
              100022861)
            </p>
          </div>
          <hr/>
          <h4 className="pt-2 text-lg font-sans font-bold mb-2">Use limitations</h4>
          <div className="font-sans text-sm mb:text-base">
            <p className="pb-4 text-sm md:text-md font-sans">
              By accessing this data outside this application or web map you
              acknowledge the use limitations this data is subject to and
              confirm that you hold an appropriate license from Ordnance Survey
              and Royal Mail. Conditions of use depend on the contract you have
              with OS and vary by product. Public sector options are summarised{' '}
              <a
                href="https://os.uk/business-government/licensing-agreements/public-sector-support"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                here
              </a>
            </p>
            <p className="pb-4 text-sm md:text-md font-sans">
              Data is used within this application with the permission of
              Ordnance Survey on behalf of the Controller of Her Majesty's
              Stationary Office Crown Copyright. Unauthorised reproduction
              infringes Crown Copyright and may lead to prosecution and/or civil
              proceedings.
            </p>
            <p className="pb-4 text-sm md:text-md font-sans">
              Any images taken or maps produced using this data must not be used
              for financial gain, and all hard copies and electronically
              published material produced from OS data must include the
              following acknowledgement:
            </p>
            <p className="pb-2 font-semibold">
              &copy; Crown copyright and database rights licensed under Defra's
              Public Sector Mapping Agreement with Ordnance Survey (licence No.
              100022861)
            </p>

            <p className="pb-4 text-sm md:text-md font-sans">
              By Clicking the Accept button on this page you agree to these
              license terms and conditions.
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="container">
          <button
            onClick={toggle}
            className="text-white focus:outline-none m-1.5 rounded px-6 py-2 font-medium bg-blue-900"
            data-testid="close-splash-screen"
            style={btnStyle}
          >
            Accept
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SplashScreen;
